import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import LeadForm from "../components/LeadForm";
import AppShowcase from "../components/AppShowcase";
import { ArrowRight, X, Check, ShieldCheck, MapPin, Smartphone, Wallet, Repeat, BadgeCheck } from "lucide-react";
import { trackCtaClick } from "../lib/analytics";

const ICONS = [ShieldCheck, Wallet, MapPin, Smartphone, Repeat, BadgeCheck];

const PENDLER_SLIDES = [
  { src: "/screenshots/pendler/A.png", alt: "Pendli-app: Park smartere. Kom i gang" },
  { src: "/screenshots/pendler/B.png", alt: "Pendli-app: Kart over parkeringsplasser i Norge" },
  { src: "/screenshots/pendler/C.png", alt: "Pendli-app: Kart med priser nær Asker stasjon" },
  { src: "/screenshots/pendler/D.png", alt: "Pendli-app: Privat parkeringsplass nær Asker stasjon" },
  { src: "/screenshots/pendler/E.png", alt: "Pendli-app: Velg pris og dato for parkering" },
  { src: "/screenshots/pendler/F.png", alt: "Pendli-app: Kollektiv i nærheten og om huseier" },
  { src: "/screenshots/pendler/H.png", alt: "Pendli-app: Betalingsoversikt og sikker betaling" },
];

export default function Pendler() {
  const { t, lang } = useLang();
  useSeo({ title: t.seo.pendler.title, description: t.seo.pendler.description, path: "/pendler", lang });

  return (
    <main data-testid="pendler-page" className="bg-ink text-cream pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="pendler-hero">
        <div className="absolute inset-0 bay-lines opacity-40 pointer-events-none" />
        <div
          className="absolute -top-20 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(232,168,0,0.16) 0%, rgba(232,168,0,0) 70%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-16 md:pt-20 md:pb-24 relative grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
          <motion.span
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 border border-amber/40 px-3 py-1.5 text-xs font-bold tracking-widest uppercase text-amber"
          >
            <span className="w-1.5 h-1.5 bg-amber rounded-full" />
            {t.pendler.heroEyebrow}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-7 font-display text-4xl sm:text-6xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tighter"
            style={{ letterSpacing: "-0.04em" }}
            data-testid="pendler-headline"
          >
            {t.pendler.heroTitle}<br />
            <span className="text-amber">{t.pendler.heroTitleAccent}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 text-base md:text-lg text-cream/70 max-w-xl leading-relaxed"
          >
            {t.pendler.heroSub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8 border-l-2 border-amber pl-4"
            data-testid="pendler-hero-stations"
          >
            <p className="text-sm md:text-base font-bold text-cream">{t.pendler.stationsLead}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.pendler.stations.map((s) => (
                <span
                  key={s}
                  data-testid={`pendler-station-${s.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 border border-amber/40 bg-amber/10 px-3 py-1 text-xs md:text-sm font-bold tracking-wide text-amber"
                >
                  <span className="w-1.5 h-1.5 bg-amber rounded-full" />
                  {s}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.25 }}
            className="mt-10"
          >
            <a
              href="#pendler-form"
              data-testid="pendler-hero-cta"
              onClick={() => trackCtaClick("pendler", "hero")}
              className="inline-flex items-center gap-3 h-14 px-6 bg-amber text-ink font-display font-bold text-base md:text-lg hover:bg-amber-hover transition-colors"
            >
              {t.pendler.heroCta} <ArrowRight size={20} />
            </a>
          </motion.div>
          </div>

          {/* App showcase */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:col-span-5 flex justify-center md:justify-end md:pt-4"
          >
            <AppShowcase slides={PENDLER_SLIDES} />
          </motion.div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-cream text-ink py-20 md:py-28" data-testid="pendler-comparison">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 md:mb-20 max-w-3xl">
            <span className="text-xs font-bold tracking-widest uppercase text-orange">Sammenligning</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
              {t.pendler.comparisonTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-0 border border-ink">
            <div className="bg-ink/5 p-6 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 bg-ink/10 flex items-center justify-center">
                  <X size={20} className="text-ink/60" strokeWidth={2.5} />
                </div>
                <h3 className="font-display text-lg md:text-2xl font-bold tracking-tight text-ink/60">{t.pendler.comparisonStation}</h3>
              </div>
              <ul className="space-y-4">
                {t.pendler.comparisons.map((c, i) => (
                  <li key={i} className="text-base md:text-lg text-ink/60 border-t border-ink/10 pt-4" data-testid={`compare-station-${i+1}`}>
                    {c.station}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-amber p-6 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 bg-ink flex items-center justify-center">
                  <Check size={20} className="text-amber" strokeWidth={3} />
                </div>
                <h3 className="font-display text-lg md:text-2xl font-bold tracking-tight">{t.pendler.comparisonPendli}</h3>
              </div>
              <ul className="space-y-4">
                {t.pendler.comparisons.map((c, i) => (
                  <li key={i} className="text-base md:text-lg font-medium border-t border-ink/20 pt-4" data-testid={`compare-pendli-${i+1}`}>
                    {c.pendli}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-ink py-20 md:py-28" data-testid="pendler-values">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter mb-14 md:mb-20 max-w-3xl" style={{ letterSpacing: "-0.04em" }}>
            {t.pendler.valuesTitle}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {t.pendler.values.map((v, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                  className="relative bg-amber-deep border border-amber/20 p-7 md:p-8 l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br"
                  data-testid={`pendler-value-${i+1}`}
                >
                  <Icon size={28} className="text-amber mb-5" strokeWidth={1.5} />
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mb-2">{v.title}</h3>
                  <p className="text-cream/70 text-sm md:text-base leading-relaxed">{v.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="pendler-form" className="bg-cream text-ink py-20 md:py-28" data-testid="pendler-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <span className="text-xs font-bold tracking-widest uppercase text-orange">{t.pendler.formEyebrow}</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
              {t.pendler.formTitle}
            </h2>
            <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">
              {t.pendler.formSub}
            </p>

            <div className="mt-10 space-y-4">
              {["Plassen står klar", "Aldri mer sirkling", "Kort gangavstand til stasjonen"].map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-orange rounded-full" />
                  <span className="text-sm md:text-base font-medium">{p}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="bg-ink p-1">
              <LeadForm
                role="pendler"
                locationLabel="station"
                source="pendler-page"
                testIdPrefix="pendler-lead"
                submitLabel={t.pendler.heroCta}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ink py-20 md:py-28 border-t border-amber/10" data-testid="pendler-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter mb-14 md:mb-16" style={{ letterSpacing: "-0.04em" }}>
            {t.pendler.faqTitle}
          </h2>
          <div className="space-y-4">
            {t.pendler.faqs.map((f, i) => (
              <details key={i} className="group border-t border-amber/20 py-5 cursor-pointer" data-testid={`pendler-faq-${i+1}`}>
                <summary className="flex items-center justify-between gap-4 list-none">
                  <h3 className="font-display text-lg md:text-2xl font-bold tracking-tight group-hover:text-amber transition-colors">
                    {f.q}
                  </h3>
                  <span className="text-amber text-2xl font-bold leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-cream/70 text-base md:text-lg leading-relaxed pr-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
